import { SynthParams, midiToFreq } from '../../core/project/types';

/**
 * SynthVoice — one polyphonic voice of the Zangle subtractive synth.
 *
 * Per-note graph:
 *
 *   fmOsc ──► fmGain ──────────────► osc1.frequency
 *   osc1 ──► g1 ─┐
 *   osc2 ──► g2 ─┴─► mix ─► filter ─► amp ─► (channel input)
 *   lfo  ──► lfoGain ──► filter.frequency | osc1/osc2.detune
 *
 * Envelopes are scheduled directly on the audio timeline (attack/decay on
 * note-on, release on note-off), so a note stays accurate even if the main
 * thread hiccups.
 */
export class SynthVoice {
  private osc1: OscillatorNode;
  private osc2: OscillatorNode;
  private fmOsc: OscillatorNode;
  private lfo: OscillatorNode;
  private amp: GainNode;
  private filter: BiquadFilterNode;
  private releaseTime: number;
  private dead = false;

  /** Called once when the voice's nodes are torn down. */
  onEnd: (() => void) | null = null;

  constructor(
    private readonly ctx: AudioContext,
    params: SynthParams,
    note: number,
    velocity: number, // 0..127
    dest: AudioNode,
    time: number,
  ) {
    const p = params;
    const f1 = midiToFreq(note) * Math.pow(2, p.osc1Octave);
    const f2 = midiToFreq(note) * Math.pow(2, p.osc2Octave);

    this.osc1 = ctx.createOscillator();
    this.osc1.type = p.osc1Wave;
    this.osc1.frequency.value = f1;
    this.osc1.detune.value = p.osc1Detune;

    this.osc2 = ctx.createOscillator();
    this.osc2.type = p.osc2Wave;
    this.osc2.frequency.value = f2;
    this.osc2.detune.value = p.osc2Detune;

    // FM: a sine at the same rate as osc1, scaled, drives osc1's pitch.
    this.fmOsc = ctx.createOscillator();
    this.fmOsc.type = 'sine';
    this.fmOsc.frequency.value = f1;
    const fmGain = ctx.createGain();
    fmGain.gain.value = p.fmDepth * 2000; // 0..2000 Hz of modulation
    this.fmOsc.connect(fmGain);
    fmGain.connect(this.osc1.frequency);

    const g1 = ctx.createGain();
    g1.gain.value = p.osc1Level;
    const g2 = ctx.createGain();
    g2.gain.value = p.osc2Level;
    const mix = ctx.createGain();
    mix.gain.value = 1;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = p.filterType;
    this.filter.frequency.value = p.filterCutoff;
    this.filter.Q.value = p.filterResonance;

    this.amp = ctx.createGain();
    this.amp.gain.value = 0;

    this.lfo = ctx.createOscillator();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = Math.max(0.05, p.lfoRate);
    const lfoGain = ctx.createGain();
    if (p.lfoTarget === 'filter') {
      lfoGain.gain.value = p.lfoDepth * 1500; // Hz around cutoff
      lfoGain.connect(this.filter.frequency);
    } else {
      lfoGain.gain.value = p.lfoDepth * 100; // cents
      lfoGain.connect(this.osc1.detune);
      lfoGain.connect(this.osc2.detune);
    }
    this.lfo.connect(lfoGain);

    this.osc1.connect(g1);
    g1.connect(mix);
    this.osc2.connect(g2);
    g2.connect(mix);
    mix.connect(this.filter);
    this.filter.connect(this.amp);
    this.amp.connect(dest);

    // ── Amp envelope ──────────────────────────────────────────────
    const vel = Math.max(0.05, velocity / 127);
    const peak = 0.22 * vel; // headroom for polyphony
    const a = Math.max(0.001, p.attack);
    const d = Math.max(0.001, p.decay);
    const g = this.amp.gain;
    g.setValueAtTime(0, time);
    g.linearRampToValueAtTime(peak, time + a);
    g.exponentialRampToValueAtTime(Math.max(peak * p.sustain, 0.0001), time + a + d);

    // ── Filter envelope (optional) ────────────────────────────────
    if (p.filterEnvAmount > 0.001) {
      const open = Math.min(18000, p.filterCutoff + p.filterEnvAmount * (18000 - p.filterCutoff));
      const fq = this.filter.frequency;
      fq.setValueAtTime(open, time);
      fq.exponentialRampToValueAtTime(Math.max(40, p.filterCutoff), time + a + d);
    }

    this.releaseTime = Math.max(0.03, p.release);
    this.osc1.start(time);
    this.osc2.start(time);
    this.fmOsc.start(time);
    this.lfo.start(time);
  }

  noteOff(time: number): void {
    if (this.dead) return;
    this.dead = true;
    const t = Math.max(time, this.ctx.currentTime);
    const g = this.amp.gain;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(0, t, this.releaseTime / 4);
    const stopAt = t + this.releaseTime + 0.1;
    const cleanup = () => {
      for (const node of [this.osc1, this.osc2, this.fmOsc, this.lfo]) {
        try {
          node.stop();
        } catch {
          /* already stopped */
        }
      }
      this.amp.disconnect();
      this.filter.disconnect();
      this.onEnd?.();
      this.onEnd = null;
    };
    const timer = this.ctx.currentTime;
    if (stopAt - timer < 60) {
      window.setTimeout(cleanup, Math.max(0, (stopAt - timer) * 1000));
    } else {
      // Safety: a voice should never outlive a minute (e.g. after hard stop).
      window.setTimeout(cleanup, 60_000);
    }
  }
}